/**
 * State for the custom digitizing service banner (DigitizingServiceBanner):
 * this browser's latest export, and when the banner was last snoozed.
 *
 * Kept per browser rather than per token, unlike editorPromoStorage: the
 * banner is for whoever is at this browser, logged out included, and a new
 * token is not a new person.
 *
 * Every storage access is wrapped. Safari's private mode, a full quota or a
 * storage-blocking policy all throw from localStorage, and a banner is not
 * worth breaking a download over -- unreadable storage just reads as "no
 * export yet", so the banner stays away.
 */

import { useEffect } from "react";

export type ExportTrigger = "create" | "editor" | "convert";

// {"trigger": ..., "at": ms}. The export that earns the banner: the latest.
const LAST_EXPORT_KEY = "digitizing_banner_last_export";
// The first version kept only the first export's page, with no time. Read as
// an export at the epoch, so a browser that exported before this change
// still counts as having exported, but not since any snooze.
const LEGACY_FIRST_EXPORT_KEY = "digitizing_banner_first_export";
const SNOOZED_AT_KEY = "digitizing_banner_dismissed_at";

// Fired on window so the banner, mounted in App, hears about exports and the
// editor promo without every page having to thread a callback up to it.
export const EXPORT_EVENT = "digitizing-service:export";
export const PROMO_MODAL_EVENT = "digitizing-service:promo-modal";

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage unavailable: nothing persists, and nothing breaks.
  }
}

function isTrigger(value: unknown): value is ExportTrigger {
  return value === "create" || value === "editor" || value === "convert";
}

interface ExportRecord {
  trigger: ExportTrigger;
  at: number;
}

/** This browser's latest export, if any. */
export function lastExport(): ExportRecord | null {
  try {
    const parsed = JSON.parse(read(LAST_EXPORT_KEY) ?? "null");
    if (parsed && isTrigger(parsed.trigger) && typeof parsed.at === "number") {
      return { trigger: parsed.trigger, at: parsed.at };
    }
  } catch {
    // Unparseable: fall through to the legacy key.
  }
  const legacy = read(LEGACY_FIRST_EXPORT_KEY);
  return isTrigger(legacy) ? { trigger: legacy, at: 0 } : null;
}

/** Call after a successful download or export. */
export function recordExport(trigger: ExportTrigger): void {
  write(LAST_EXPORT_KEY, JSON.stringify({ trigger, at: Date.now() }));
  window.dispatchEvent(new CustomEvent(EXPORT_EVENT));
}

/**
 * Hide the banner for `digitizing_banner_snooze_hours`: after a dismissal,
 * and after a quote request, sent or failed (they have found the form).
 */
export function snoozeBanner(): void {
  write(SNOOZED_AT_KEY, String(Date.now()));
}

// When the latest snooze runs out, or null if the banner was never snoozed.
function snoozeEnds(snoozeHours: number): number | null {
  const snoozedAt = Number(read(SNOOZED_AT_KEY));
  return snoozedAt ? snoozedAt + snoozeHours * 60 * 60 * 1000 : null;
}

/** Whether a dismissal (or quote request) is still holding the banner back. */
export function bannerSnoozed(snoozeHours: number): boolean {
  const ends = snoozeEnds(snoozeHours);
  return ends !== null && Date.now() < ends;
}

/**
 * Whether the banner has been earned away from /create, where every visitor
 * sees it: there is an export, and if the banner was snoozed, the snooze has
 * run out *and* an export has happened since it did. Running out alone
 * doesn't bring it back -- it returns with the next export, never on a page
 * load out of nowhere.
 */
export function bannerEarned(snoozeHours: number): boolean {
  const exported = lastExport();
  if (!exported) return false;
  const ends = snoozeEnds(snoozeHours);
  if (ends === null) return true;
  return Date.now() >= ends && exported.at >= ends;
}

// How many post-download modals are on screen: the editor promo, the design
// email dialog, the Pro card. The banner never appears alongside one -- it
// waits until the last has closed -- so each holds it while mounted, through
// useHoldDigitizingBanner. A count rather than a flag because they can hand
// over to each other, and a close must not release a hold another still has.
let modalsOpen = 0;

export function isPromoModalOpen(): boolean {
  return modalsOpen > 0;
}

export function useHoldDigitizingBanner(): void {
  useEffect(() => {
    modalsOpen += 1;
    window.dispatchEvent(new CustomEvent(PROMO_MODAL_EVENT));
    return () => {
      modalsOpen -= 1;
      window.dispatchEvent(new CustomEvent(PROMO_MODAL_EVENT));
    };
  }, []);
}
